import React, { useEffect, useState, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { fetchTransactions } from "../utils/api"
import api from "../utils/api"
import { io, Socket } from "socket.io-client"
import "./Navbar.css"
import logo from '../assets/logo.png'

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [hasPendingTransactions, setHasPendingTransactions] = useState(false)
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)
  const socketRef = useRef<Socket | null>(null)


  useEffect(() => {
    const checkTransactions = async () => {
      if (!isAuthenticated || !user) return
      try {
        const response = await fetchTransactions()
        const pendingExists = response.data.transactions.some(
          (tx: any) =>
            tx.status === "pending" &&
            (tx.buyer_id === user.id || tx.seller_id === user.id)
        )
        setHasPendingTransactions(pendingExists)
      } catch (err) {
        console.error("Error checking transactions:", err)
      }
    }

    const checkUnreadMessages = async () => {
      if (!isAuthenticated || !user) return
      try {
        const response = await api.get("/chat/conversations")
        const conversations = response.data.conversations
        const hasUnread = conversations.some((conv: any) => {
          const msg = conv.last_message
          return msg && msg.receiver_id === user.id && !msg.is_read
        })
        setHasUnreadMessages(hasUnread)
      } catch (err) {
        console.error("Error checking unread messages:", err)
      }
    }

    // Initial data
    checkTransactions()
    checkUnreadMessages()

    // Setup socket connection
    if (isAuthenticated && user) {
      const token = localStorage.getItem("token")
      const socket = io("https://utm-emart-deployment-backend.onrender.com", {
        auth: { token },
        transports: ["websocket"],
      })
      socketRef.current = socket

      socket.on("connect", () => {
        console.log("✅ Navbar connected to socket.io")
      })

      // Live chat updates
      socket.on("new_message", (message: any) => {
        if (message.receiver_id === user.id) {
          setHasUnreadMessages(true)
        }
      })

      // Live transaction updates
      socket.on("new_transaction", (data: any) => {
        if (data.buyer_id === user.id || data.seller_id === user.id) {
          setHasPendingTransactions(true)
        }
      })

      return () => {
        socket.disconnect()
      }
    }
  }, [isAuthenticated, user])

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  return (
    <nav className="navbar">
      <div className="container navbar-container">
        <div className="navbar-brand">
          <div className="navbar-logo-container">
            <img src={logo} alt="Maackia's Cult Logo" className="navbar-logo-image" />
          </div>
          <Link to="/" className="navbar-logo">MAACKIA'S CULT</Link>
          <div className="navbar-slogan">"i will bathe in your blood and revel in your screams"</div>
        </div>

        <div className="navbar-links">
          <Link to="/" className="navbar-link">Home</Link>
          <Link to="/about" className="navbar-link">About Us</Link>

          {isAuthenticated && user?.access_level === "seller" && (
            <>
              <Link to="/your-products" className="navbar-link">My Products</Link>
              <Link to="/publish-product" className="navbar-link">Publish Product</Link>
            </>
          )}

          {isAuthenticated && (
            <>
              <Link to="/chat" className={`navbar-link ${hasUnreadMessages ? "bold" : ""}`}>
                Chat
                {hasUnreadMessages && <span className="white-dot" />}
              </Link>

              <Link to="/transactions" className={`navbar-link ${hasPendingTransactions ? "bold" : ""}`}>
                Transactions
                {hasPendingTransactions && <span className="white-dot" />}
              </Link>

              <div className="navbar-dropdown">
                <button className="navbar-link dropdown-toggle">
                  Profile
                  {user?.username && <span className="navbar-username">({user.username})</span>}
                </button>
                <div className="dropdown-menu">
                  <Link to="/edit-profile" className="dropdown-item">Edit Profile</Link>
                  {user?.access_level === "admin" && (
                    <Link to="/dashboard" className="dropdown-item">Dashboard</Link>
                  )}
                  <button onClick={handleLogout} className="dropdown-item">Logout</button>
                </div>
              </div>
            </>
          )}

          {!isAuthenticated && (
            <>
              <Link to="/login" className="navbar-link">Login</Link>
              <Link to="/register" className="navbar-link">Register</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}

export default Navbar
